import React, { useState } from "react";
import { updateCompetitionMatch } from "../../../api";
import { CompetitionStage, ID, Match, TournamentParticipant } from "../../../types";
import { formatScheduledTime } from "../../../utils/datetime";
import { Button } from "../Button";
import { MatchCard } from "./MatchCard";
import { MatchEditModal } from "./MatchEditModal";
import { useTournamentEvent } from "./TournamentEventContext";

interface MatchCardsListProps {
  matches: Match[];
  stageMatches: Map<ID, Match>;
  participantsById: Map<ID, TournamentParticipant>;
  participants: TournamentParticipant[];
  getSlotLabel: (match: Match, slot: 1 | 2) => string;
  stagesById?: Map<ID, CompetitionStage>;
  showStageName?: boolean;
  emptyMessage?: string;
}

export const MatchCardsList: React.FC<MatchCardsListProps> = ({
  matches,
  stageMatches,
  participantsById,
  participants,
  getSlotLabel,
  stagesById,
  showStageName = false,
  emptyMessage = "Nessun match disponibile.",
}) => {
  const { canManage, enableCalendar, tournamentName, onResultSaved } = useTournamentEvent();
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);

  const markScheduled = async (match: Match) => {
    try {
      await updateCompetitionMatch(match.id, {
        status: "scheduled",
        winner: null,
        score1: null,
        score2: null,
      });
      await onResultSaved();
    } catch (error) {
      console.error(error);
      alert("Errore nell'aggiornamento dello stato della partita: " + error);
    }
  };

  const markOngoing = async (match: Match) => {
    if (match.player1 == null || match.player2 == null) {
      alert("Seleziona entrambi i partecipanti dalla modale prima di disputare il match.");
      return;
    }
    try {
      await updateCompetitionMatch(match.id, {
        status: "ongoing",
        winner: null,
        score1: null,
        score2: null,
      });
      await onResultSaved();
    } catch (error) {
      console.error(error);
      alert("Errore nell'aggiornamento dello stato della partita: " + error);
    }
  };

  const canReplayMatch = (match: Match) => {
    if (!canManage) return false;

    const nextWinnerMatch = match.next_winner_match;
    const nextLoserMatch = match.next_loser_match;

    if (nextWinnerMatch === null || nextLoserMatch === null) return true;

    return (
      stageMatches.get(nextWinnerMatch)?.status === "scheduled" &&
      stageMatches.get(nextLoserMatch)?.status === "scheduled"
    );
  };

  return (
    <div className="space-y-3">
      {matches.map((match) => {
        const player1 = match.player1 ? participantsById.get(match.player1)?.name : null;
        const player2 = match.player2 ? participantsById.get(match.player2)?.name : null;
        const hasPlayers = Boolean(player1 && player2);
        const scheduledTime = enableCalendar ? formatScheduledTime(match.scheduled_at) : null;
        const stage = showStageName && stagesById ? stagesById.get(match.stage) : null;

        return (
          <MatchCard
            key={match.id}
            match={match}
            player1Label={player1 ?? getSlotLabel(match, 1)}
            player2Label={player2 ?? getSlotLabel(match, 2)}
            timeLabel={scheduledTime}
            stageLabel={stage?.name}
            actions={
              canManage ? (
                <>
                  {match.status === "scheduled" && hasPlayers && (
                    <Button size="sm" variant="success" onClick={() => markOngoing(match)}>
                      Disputa
                    </Button>
                  )}
                  {canReplayMatch(match) && match.status === "played" && (
                    <Button size="sm" onClick={() => markScheduled(match)}>
                      Rigioca
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setEditingMatch(match)}>
                    Modifica
                  </Button>
                </>
              ) : undefined
            }
          />
        );
      })}
      {matches.length === 0 ? (
        <div className="rounded border border-gray-300 p-3 text-center text-sm text-gray-500">{emptyMessage}</div>
      ) : null}
      {editingMatch && (
        <MatchEditModal
          match={editingMatch}
          participants={participants}
          getSlotLabel={getSlotLabel}
          enableCalendar={enableCalendar}
          tournamentName={tournamentName}
          onClose={() => setEditingMatch(null)}
          onSaved={onResultSaved}
        />
      )}
    </div>
  );
};
